import { useEffect, useRef, useState } from 'react';
import { Download, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardTitle } from '@/components/ui/Card';
import { Input, Label } from '@/components/ui/Input';
import { Banner } from '@/components/ui/Banner';
import { useToast } from '@/components/ui/Toast';
import { useAuthStore } from '@/stores/auth';
import { adminApi } from '@/services/admin.api';

/**
 * Onglet Export (admin-only) — export CSV brut prospects + opportunités.
 *
 * Décision produit 2026-04-28 :
 *  - Périmètre strictement `prospects` + `opportunities` (pas de relations).
 *  - Format CSV brut, UTF-8 sans BOM, séparateur virgule.
 *  - Soft-deletes inclus, données démo incluses.
 *  - Double-facteur soft : ré-saisie du code TOTP avant déclenchement.
 *
 * UX :
 *  - Card explicative + bouton « Exporter prospects et opportunités »
 *  - Clic → modale Stripe-like avec input 6 chiffres TOTP (auto-focus)
 *  - Submit → POST → blob ZIP → download programmatique navigateur
 *  - Erreurs 401 (TOTP invalide) affichées inline dans la modale
 */
export function ExportTab() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="space-y-4">
      <Banner tone="warning">
        <strong>Action sensible.</strong> L&apos;export contient toutes les données brutes des
        prospects et des opportunités, y compris les soft-deletes. Conservez le fichier dans un
        emplacement chiffré et supprimez-le après usage.
      </Banner>

      <Card>
        <CardTitle>Exporter les données</CardTitle>
        <p className="mt-1 text-sm text-text-muted">
          Génère un fichier ZIP contenant deux CSV bruts :
          <code className="mx-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs">prospects.csv</code>
          et
          <code className="mx-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs">opportunities.csv</code>.
          Toutes les colonnes sont exportées telles que stockées en base (UUID, dates ISO 8601,
          énumérations, décimaux). La table
          <em> users</em> et les champs sensibles (mots de passe, secrets MFA) ne sont jamais
          inclus.
        </p>
        <div className="mt-4 flex items-center gap-3">
          <Button
            variant="primary"
            leadingIcon={<Download className="h-3.5 w-3.5" />}
            onClick={() => setModalOpen(true)}
          >
            Exporter prospects et opportunités
          </Button>
          <span className="flex items-center gap-1 text-xs text-text-muted">
            <ShieldCheck className="h-3 w-3" />
            Code de vérification TOTP requis
          </span>
        </div>
      </Card>

      {modalOpen && <ExportConfirmModal onClose={() => setModalOpen(false)} />}
    </div>
  );
}

function ExportConfirmModal({ onClose }: { onClose: () => void }) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const isValid = /^\d{6}$/.test(code);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { blob, filename } = await adminApi.exportData(token, code);
      // Déclenche le download navigateur via un <a download> programmatique.
      // Object URL libéré dans le tick suivant pour ne pas couper le DL.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast({ tone: 'success', message: 'Export téléchargé.' });
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur inattendue';
      // Code TOTP invalide → message inline dans la modale ;
      // autres erreurs → toast rouge + close.
      if (msg.includes('TOTP') || msg.includes('totp')) {
        setError("Code invalide. Vérifie ton application d'authentification.");
      } else {
        toast({ tone: 'danger', title: 'Export impossible', message: msg });
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="animate-fade-in fixed inset-0 z-50 flex items-center justify-center bg-brand/30 px-4 backdrop-blur-sm"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-modal"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/10 text-accent">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-brand">Confirmer l&apos;export</h2>
            <p className="text-xs text-text-muted">
              Saisis ton code de vérification à 6 chiffres pour continuer.
            </p>
          </div>
        </div>

        <div className="mt-5">
          <Label htmlFor="totp-code">Code de vérification</Label>
          <Input
            id="totp-code"
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => {
              // Filtre tout sauf les chiffres + cap à 6.
              const cleaned = e.target.value.replace(/\D/g, '').slice(0, 6);
              setCode(cleaned);
              if (error) setError(null);
            }}
            placeholder="000000"
            className="text-center text-lg tracking-[0.4em] font-mono"
            disabled={submitting}
          />
          {error && <p className="mt-1.5 text-xs text-danger-700">{error}</p>}
          <p className="mt-2 text-[11px] text-text-muted">
            Code généré par ton application d&apos;authentification (Google Authenticator, Microsoft
            Authenticator, 1Password…).
          </p>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>
            Annuler
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!isValid}
            loading={submitting}
            leadingIcon={!submitting ? <Download className="h-3.5 w-3.5" /> : undefined}
          >
            Télécharger
          </Button>
        </div>
      </form>
    </div>
  );
}
